import { useEffect, useRef, useState } from 'react';
import { findIdFromUrl, sendUsageRequest } from './functions';
import { AccessStore } from '../store/Access';
import { useNavigate } from 'react-router';
import { HOME_ROUTE } from './consts';
import Api from '../api/API';
import { useAnalyticsEventTracker } from '../analytics/analytics';
import { FilterElement, FiltersPages } from '../types';
import { FiltersStore } from '../store/Filters';
import { useWindowSize } from 'react-use';
import { BREAKPOINT_MD } from '../consts';

export function useUpdateEffect<T>(effect: () => void, deps: T): void {
  const isFirstMount = useFirstMountState();

  useEffect(() => {
    if (!isFirstMount) {
      return effect();
    }
  }, [deps]);
}

export function useUsageStatistics(page: string): void {
  useEffect(() => {
    sendUsageRequest(page);
  }, []);
}

export function useFirstMountState():boolean{
  const isFirst = useRef(true);

  if (isFirst.current) {
    isFirst.current = false;

    return true;
  }

  return isFirst.current;
}

type accessTypeT = 'APPROVER' | 'CALENDAR_ADMIN'
export const useCheckAccess = (accessType: accessTypeT) => {
  const [isAccessAllowed, setIsAccessAllowed] = useState(false);
  const {allAccesses, isAllAccessesFetched } = AccessStore;

  useEffect(() => {
    allAccesses.forEach(i => {
      if(accessType === 'APPROVER') {
        i.isApproveAllowed && setIsAccessAllowed(i.isApproveAllowed);
      }
      if(accessType === 'CALENDAR_ADMIN') {
        i.isModificationAllowed && setIsAccessAllowed(i.isModificationAllowed);
      }
    });
  },[allAccesses]);

  return {
    isAllowedAccess: isAccessAllowed,
    isAllAccessesFetched: isAllAccessesFetched
  };

};


export const useCloseModal = (close:()=> void, url: string) => {
  const navigate = useNavigate();
  const id = findIdFromUrl(url);

  return () => {
    close();
    id && navigate(HOME_ROUTE);
  };
};


export const useDownloadItem = () => {
  const [isLoading, setIsLoading] = useState(false);

  const download = (name: string, ref: string) => Api<any,any>().get(`${process.env.REACT_APP_LM_API_URL}/${ref}`)
    .then((response) => {
      setIsLoading(true);
      return response.blob();
    }).then((blob) => {
      const url = window.URL.createObjectURL(
        new Blob([blob])
      );
      const link = document.createElement('a');
      link.href = url;
      link.setAttribute(
        'download',
        `${name}`
      );
      document.body.appendChild(link);
      link.click();
      link?.parentNode?.removeChild(link);
      setIsLoading(false);
    });

  return {isLoading: isLoading, download: download};
};

export const useAnalytics = (category: string, report_action: string,  report_Name: string) => {
  const gaEventTracker = useAnalyticsEventTracker(category);

  gaEventTracker(report_action, report_Name);
};

export const useFilters = (type : FiltersPages) => {
  const {filters, setFilters} = FiltersStore;
  const onFiltersChange = (value: FilterElement) => {
    const added = filters[type].blockOfFilters.map(i => i.element === value.element ? {
      element: i.element,
      value: value.value
    }
      :
      i
    );
    setFilters({...filters[type], blockOfFilters: [...added]});
  };

  return {
    onFilterChange: onFiltersChange
  };
};

export const useIsMobile = (): boolean => {
  const { width } = useWindowSize();
  return (width < BREAKPOINT_MD);
};

export const useDeviceSizeThreshold = (breakPoint: number): boolean => {
  const { width } = useWindowSize();
  return (width < breakPoint);
};

export const useDebouncedValue = <T>(value: T, delay = 500) => {
  const [debouncedValue, setDebouncedValue] = useState(value);

  useEffect(() => {
    const timeoutId = setTimeout(() => {
      setDebouncedValue(value);
    }, delay);

    return () => {
      clearTimeout(timeoutId);
    };
  }, [value, delay]);

  return debouncedValue;
};